import * as React from "react"
import { ChevronDown, ChevronUp, X, Sparkles } from "lucide-react"
import { IconButton } from "@/components/ui/icon-button"
import { ResizeHandle } from "@/components/ui/resize-handle"
import { cn } from "@/lib/utils"
import type { CopilotCheckpoints, CopilotRecommendation, RecommendationPriority } from "../../hooks/useCopilot"
import { CheckpointList } from "./CheckpointList"

export const DEMO_DIALOG_RECORDING_BADGE = "Звучит демо-запись диалога"

const PRIORITY_BORDER: Record<RecommendationPriority, string> = {
    high: "border-l-state-negative",
    medium: "border-l-state-warning",
    low: "border-l-state-positive",
}

const MIN_HEIGHT = 120
const MAX_HEIGHT = 480
const DEFAULT_HEIGHT = 240

interface CopilotPanelProps {
    recommendations: CopilotRecommendation[]
    checkpoints: CopilotCheckpoints
    showCheckpoints?: boolean
    /** After stop/end: unfinished checkpoints render as red CircleX. */
    showMissedCheckpoints?: boolean
    isActive: boolean
    /** Interactive tour: scripted dialog audio is playing (not the mic). */
    isDemoRecording?: boolean
    error?: string | null
    onClear?: () => void
    className?: string
    registerTourTarget?: (id: string, el: HTMLElement | null) => void
}

export function CopilotPanel({
    recommendations,
    checkpoints,
    showCheckpoints = true,
    showMissedCheckpoints = false,
    isActive,
    isDemoRecording = false,
    error,
    onClear,
    className,
    registerTourTarget,
}: CopilotPanelProps) {
    const [isCollapsed, setIsCollapsed] = React.useState(false)
    const [panelHeight, setPanelHeight] = React.useState(DEFAULT_HEIGHT)
    const [isDragging, setIsDragging] = React.useState(false)

    const panelHeightRef = React.useRef(panelHeight)
    React.useEffect(() => {
        panelHeightRef.current = panelHeight
    }, [panelHeight])

    // Oldest recommendation stays at the end (newest are prepended).
    const firstRecommendationId =
        recommendations.length > 0 ? recommendations[recommendations.length - 1].id : null

    const handleDragStart = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        const startY = e.clientY
        const startHeight = panelHeightRef.current
        setIsDragging(true)

        const onMouseMove = (ev: MouseEvent) => {
            const delta = startY - ev.clientY
            const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startHeight + delta))
            setPanelHeight(newHeight)
        }

        const onMouseUp = () => {
            setIsDragging(false)
            window.removeEventListener("mousemove", onMouseMove)
            window.removeEventListener("mouseup", onMouseUp)
        }

        window.addEventListener("mousemove", onMouseMove)
        window.addEventListener("mouseup", onMouseUp)
    }, [])

    const isEmpty = recommendations.length === 0

    return (
        <div
            className={cn(
                "flex flex-col rounded-l border border-neutral-border-300 bg-neutral-background-200 overflow-hidden",
                !isDragging && "transition-[height] duration-200",
                className,
            )}
            style={{ height: isCollapsed ? "40px" : `${panelHeight}px` }}
        >
            {!isCollapsed && <ResizeHandle onMouseDown={handleDragStart} />}

            {/* Header */}
            <div className="flex h-9 shrink-0 items-center justify-between gap-2 px-3 border-b border-neutral-border-300">
                <div className="flex min-w-0 items-center gap-2">
                    <Sparkles className="size-3.5 text-primary-400 dark:text-primary-600 shrink-0" strokeWidth={2} />
                    <span className="text-body2 font-medium text-neutral-text-900">Copilot</span>
                    {isActive && (
                        <span className="flex shrink-0 items-center gap-1 text-caption text-state-positive">
                            <span className="size-1.5 rounded-full bg-state-positive animate-pulse" />
                            в эфире
                        </span>
                    )}
                    {isDemoRecording && (
                        <span
                            className="min-w-0 truncate text-caption text-neutral-text-500"
                            title={DEMO_DIALOG_RECORDING_BADGE}
                        >
                            {DEMO_DIALOG_RECORDING_BADGE}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {!isEmpty && onClear && (
                        <IconButton
                            variant="ghost"
                            size="xs"
                            onClick={onClear}
                            className="flex h-6 w-6 items-center justify-center rounded-s text-neutral-text-500 hover:bg-neutral-background-100 hover:text-neutral-text-900 transition-colors"
                            aria-label="Очистить"
                        >
                            <X className="size-3.5" strokeWidth={2.5} />
                        </IconButton>
                    )}
                    <IconButton
                        variant="ghost"
                        size="xs"
                        onClick={() => setIsCollapsed((v) => !v)}
                        className="flex h-6 w-6 items-center justify-center rounded-s text-neutral-text-500 hover:bg-neutral-background-100 hover:text-neutral-text-900 transition-colors"
                        aria-label={isCollapsed ? "Развернуть" : "Свернуть"}
                    >
                        {isCollapsed ? (
                            <ChevronUp className="size-3.5" strokeWidth={2.5} />
                        ) : (
                            <ChevronDown className="size-3.5" strokeWidth={2.5} />
                        )}
                    </IconButton>
                </div>
            </div>

            {/* Content */}
            {!isCollapsed && (
                <div className="flex flex-1 min-h-0">
                    {showCheckpoints && (
                        <div className="w-1/3 shrink-0 border-r border-neutral-border-300 overflow-y-auto px-2 py-2">
                            <CheckpointList
                                checkpoints={checkpoints}
                                showMissed={showMissedCheckpoints}
                                registerTourTarget={registerTourTarget}
                            />
                        </div>
                    )}
                    <div className="flex-1 min-w-0 overflow-y-auto px-3 py-2 space-y-2">
                        {error ? (
                            <div className="flex h-full flex-col items-center justify-center gap-1 text-body2 text-state-negative">
                                {error}
                            </div>
                        ) : isEmpty ? (
                            <div className="flex h-full items-center justify-center text-neutral-text-500 text-body2">
                                {isActive ? "Ожидание рекомендаций..." : "Начните запись"}
                            </div>
                        ) : (
                            recommendations.map((rec) => (
                                <RecommendationCard
                                    key={rec.id}
                                    rec={rec}
                                    registerTourTarget={
                                        rec.id === firstRecommendationId
                                            ? registerTourTarget
                                            : undefined
                                    }
                                />
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

function RecommendationCard({
    rec,
    registerTourTarget,
}: {
    rec: CopilotRecommendation
    registerTourTarget?: (id: string, el: HTMLElement | null) => void
}) {
    const cardRef = React.useCallback(
        (el: HTMLDivElement | null) => {
            registerTourTarget?.("copilot-first-recommendation", el)
        },
        [registerTourTarget],
    )

    return (
        <div
            ref={registerTourTarget ? cardRef : undefined}
            data-tour-target={registerTourTarget ? "copilot-first-recommendation" : undefined}
            className={cn("rounded-s border-l-2 bg-neutral-background-100 px-3 py-2", PRIORITY_BORDER[rec.priority])}
        >
            <p className="text-body2 text-neutral-text-900">{rec.text}</p>
        </div>
    )
}
